import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { SpiralIcon } from './icons/AlchemyIcons';
import { toast } from 'sonner';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password change modal state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [userData, setUserData] = useState(null);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
      // This completely eliminates "body is disturbed or locked" and "clone" errors
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Login response was not JSON:', responseText);
        throw new Error('Server returned an unexpected response. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }
      
      // Check if user must change password
      if (data.must_change_password) {
        setUserData(data);
        setCurrentPassword(password); // Store current password for the change
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Normal login - save and redirect
      localStorage.setItem('workbook_user_id', data.user_id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', email);
      
      window.location.href = '/dashboard';
    } catch (error) {
      // Handle specific error messages with user-friendly guidance
      let userMessage = error.message;
      
      if (error.message.includes('disturbed') || error.message.includes('locked') || error.message.includes('clone') || error.message.includes('already used')) {
        userMessage = 'Connection issue detected. Please refresh the page and try again.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if (!userMessage || userMessage === 'Failed to fetch') {
        userMessage = 'Login failed. Please refresh the page and try again. If you just registered, your password is "password" (without quotes).';
      }
      
      alert(userMessage);
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (newPassword === 'password') {
      setPasswordError('Please choose a stronger password');
      return;
    }
    
    setLoading(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/users/change-password-required`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      // BULLETPROOF FIX: Read body ONLY ONCE as text, then parse
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Password change response error:', responseText);
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }
      
      // Password changed successfully - save user data and redirect
      localStorage.setItem('workbook_user_id', data.user_id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', email);
      
      alert('Password changed successfully! Redirecting to dashboard...');
      window.location.href = '/dashboard';
      
    } catch (error) {
      setPasswordError(error.message);
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSendingMessage(true);
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/contact/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          message: contactMessage,
          source: 'login_page'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      toast.success('Message sent! We\'ll get back to you soon. 💛');
      setShowContactForm(false);
      setContactName('');
      setContactEmail('');
      setContactMessage('');
    } catch (error) {
      toast.error('Failed to send message. Please try emailing us directly at hello@defiancesprings.com');
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
      {/* Password Change Modal */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="max-w-md" style={{ borderRadius: 0 }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Create Your New Password
            </DialogTitle>
          </DialogHeader>
          <div className="text-center mb-4">
            <p className="text-gray-600">Welcome, <strong>{userData?.name}</strong>!</p>
            <p className="text-sm text-gray-500 mt-1">Please create a secure password for your account.</p>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                required
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="confirm-new-password-input"
              />
            </div>
            {passwordError && (
              <p className="text-red-600 text-sm text-center">{passwordError}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-black/90 py-4"
              style={{ borderRadius: 0 }}
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? 'Saving...' : 'Set New Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Container for notification and card */}
      <div className="w-full max-w-md px-4 space-y-4">
        {/* Cute Building Notice with Contact */}
        <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-200/50 rounded-lg p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <SpiralIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-900 leading-relaxed mb-2">
                <span className="font-semibold">We're still building out user accounts!</span> 🌱 Please bear with us as we perfect your experience. Your feedback helps us grow—let us know what works and what doesn't. We're here to make this journey beautiful for you. 💛
              </p>
              <button
                onClick={() => setShowContactForm(true)}
                className="text-xs font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors"
              >
                Send us feedback →
              </button>
            </div>
          </div>
        </div>

        {/* Contact/Feedback Dialog */}
        <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
          <DialogContent className="max-w-md" style={{ borderRadius: 0 }}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                Send Us Feedback
              </DialogTitle>
            </DialogHeader>
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">We'd love to hear from you! Let us know about any issues or suggestions.</p>
            </div>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Your Name *</Label>
                <Input
                  required
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="Enter your name"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div className="space-y-2">
                <Label>Your Email *</Label>
                <Input
                  type="email"
                  required
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  required
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Tell us what's on your mind..."
                  className="border-black min-h-[120px]"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-black text-white hover:bg-black/90 py-4"
                style={{ borderRadius: 0 }}
                disabled={sendingMessage}
              >
                {sendingMessage ? 'Sending...' : 'Send Feedback'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Login Card */}
        <Card className="w-full" style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-center">Member Login</CardTitle>
            <p className="text-center text-gray-600 mt-2">The Belonging Sessions</p>
          </CardHeader>
          <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-black"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="login-password"
              />
            </div>
            <div className="text-right">
              <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                Forgot your password?
              </a>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-black/90 py-6 text-lg"
              style={{ borderRadius: 0 }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="text-center pt-4 space-y-2">
              <a href="/" className="text-sm text-gray-600 hover:underline block">
                ← Back to Home
              </a>
              <p className="text-xs text-gray-500">
                Don't have an account? <a href="/" className="text-blue-600 hover:underline">Register here</a>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}

export default Login;
